/**
 * Funnel session persistence — survives iframe parent-page refreshes.
 *
 * The public widget runs inside an iframe on hellomortgage.ca. When the
 * visitor finishes the funnel we navigate the iframe to /compare?lead=xyz,
 * but a parent-page refresh reloads the iframe at its starting `src` URL
 * (typically `?embed=core`), wiping the route. Without persistence the
 * visitor lands back on the home widget and is forced to re-fill the form.
 *
 * We stash the minimum needed to resume — the leadId plus the Compare query
 * params — in localStorage with a 24-hour TTL. On iframe boot, Widget.jsx
 * checks for a saved session and auto-redirects to /compare if found and
 * still valid. Compare.jsx clears the session on genuine 404 (lead purged).
 *
 * Why localStorage and not a cookie? Same-origin iframe localStorage works
 * across embed reloads in every modern browser (third-party cookies are
 * increasingly blocked, localStorage on the iframe's own origin is not).
 */

const STORAGE_KEY = "hm_funnel_resume_v1";
const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function safeStorage() {
  if (typeof window === "undefined") return null;
  try {
    const probe = "__hm_probe__";
    window.localStorage.setItem(probe, probe);
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    // Safari private mode / storage blocked → silently no-op.
    return null;
  }
}

export function saveFunnelSession(state) {
  const ls = safeStorage();
  if (!ls) return;
  if (!state || !state.leadId) return;
  try {
    ls.setItem(STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch {
    // Storage quota / serialization edge — non-fatal.
  }
}

export function loadFunnelSession() {
  const ls = safeStorage();
  if (!ls) return null;
  let raw;
  try {
    raw = ls.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    ls.removeItem(STORAGE_KEY);
    return null;
  }
  if (!parsed || !parsed.leadId) return null;
  if (Date.now() - (parsed.savedAt || 0) > TTL_MS) {
    ls.removeItem(STORAGE_KEY);
    return null;
  }
  return parsed;
}

export function clearFunnelSession() {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}
